import { useMemo } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import { Header } from 'app/KinerjaShared/components/Header'
import CardPeriod from 'app/KinerjaShared/components/CardPeriod'
import { IndicatorCard } from 'app/KinerjaPage/pages/PelaksanaanV2/components/IndicatorCard'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import { useAPIPelaksanaanProgress } from 'api/kinerja/useAPIKinerja'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { PageGuardGpkPelaksanaanStatus } from 'app/KinerjaGpkPage/components/PageGuardGpkPelaksanaanStatus'
import { PersiapanStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/PersiapanStepCard'
import { ObservasiStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/ObservasiStepCard'
import { PelaksanaanTindakLanjutStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/PelaksanaanTindakLanjutStepCard'
import { RefleksiEvaluasiStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/RefleksiEvaluasiStepCard'
import {
  SectionCard,
  SectionCardHeader,
  SectionCardBody,
} from 'app/KinerjaShared/components/SectionCard'
import { GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS } from 'app/KinerjaGpkPage/constants/contents'
import { RencanaTindakLanjutStepCard } from 'app/KinerjaGpkPage/pages/pegawai/pelaksanaan/HubPraktikKinerja/RencanaTindakLanjutStepCard'
import { PRAKTIK_KINERJA_STEP_ORDER } from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'

import type { MultiPeriodPayload } from 'types/kinerja'

type HubPraktikKinerjaProps = {
  periodParams?: MultiPeriodPayload
}

export function HubPraktikKinerja({ periodParams }: HubPraktikKinerjaProps) {
  const { tipe } = useKinerjaTipe()
  const { periodNo, periodYear } = usePeriodContext()

  // -----------------------------------------
  // Resolve period params
  // -----------------------------------------
  const resolvedPeriodParams = useMemo(() => {
    if (periodParams) return periodParams

    // Default with current period
    return {
      skpPeriod: periodNo,
      skpYear: periodYear,
    }
  }, [periodParams, periodNo, periodYear])

  // -----------------------------------------
  // Get pelaksanaan progress data
  // -----------------------------------------
  const {
    isLoading: isPelaksanaanProgressLoading,
    data: pelaksanaanProgressData,
  } = useAPIPelaksanaanProgress({
    tipe,
    periodParams: {
      skp_period: resolvedPeriodParams.skpPeriod,
      skp_year: resolvedPeriodParams.skpYear,
    },
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const currentState = pelaksanaanProgressData?.data?.state

  // -----------------------------------------
  // Resolve Praktik Kinerja asset path
  // -----------------------------------------
  const illustrationAssetPath = useMemo(() => {
    const icon =
      GPK_PEGAWAI_PRAKTIK_KINERJA_ICONS[
        pelaksanaanProgressData?.data?.subIndicatorName
      ]

    if (icon) return assetUrl(`/illustrations/${icon}`)

    return ''
  }, [pelaksanaanProgressData?.data?.subIndicatorName])

  // -----------------------------------------
  // Check if pelaksanaan is completed
  // -----------------------------------------
  const isPelaksanaanCompleted = useMemo(() => {
    const currentStep = pelaksanaanProgressData?.data?.state
    const currentStepIndex =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentStep) ?? 0
    const minimalStepToComplete = 'REFLECTION_FOLLOW_UP_EVALUATION'
    const minimalStepToCompleteIndex =
      PRAKTIK_KINERJA_STEP_ORDER.indexOf(minimalStepToComplete) ?? Infinity

    const isCompleted = currentStepIndex >= minimalStepToCompleteIndex

    return isCompleted
  }, [pelaksanaanProgressData?.data?.state])

  return (
    <main className="px-8 pt-12 pb-8">
      <div className="mx-auto max-w-7xl">
        <PageGuardGpkPelaksanaanStatus periodParams={resolvedPeriodParams}>
          <div className="flex items-center justify-between">
            <Header title="Praktik Kinerja" className="mb-9" />
          </div>

          {isPelaksanaanCompleted && <CompletedPelaksanaanBanner />}

          <SectionCard>
            <SectionCardHeader variant="gradient-blue">
              <Text as="h2" color="inherit" variant="heading-lg">
                Periode:{' '}
                <CardPeriod
                  period={resolvedPeriodParams?.skpPeriod}
                  year={resolvedPeriodParams?.skpYear}
                  enableMultiplePeriodSupport
                />
              </Text>
            </SectionCardHeader>
            <SectionCardBody className="p-6 space-y-12">
              <IndicatorCard
                illustrationAssetPath={illustrationAssetPath}
                subIndicatorName={
                  pelaksanaanProgressData?.data?.subIndicatorName
                }
                subIndicatorDefinition={
                  pelaksanaanProgressData?.data?.subIndicatorDefinition
                }
                qualityTarget={pelaksanaanProgressData?.data?.qualityTarget}
                contentId={pelaksanaanProgressData?.data?.contentId}
                isLoading={isPelaksanaanProgressLoading}
              />

              <div className="space-y-14">
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 1: Pelaksanaan Observasi
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PersiapanStepCard
                      appealState={pelaksanaanProgressData?.data?.appealState}
                      latestFormState={
                        pelaksanaanProgressData?.data?.latestFormState
                      }
                      currentState={currentState}
                    />

                    <StepArrow />

                    <ObservasiStepCard currentState={currentState} />

                    <StepArrow />

                    <RencanaTindakLanjutStepCard
                      appealState={pelaksanaanProgressData?.data?.appealState}
                      currentState={currentState}
                      latestFormState={
                        pelaksanaanProgressData?.data?.latestFormState
                      }
                    />
                  </div>
                </div>
                <div>
                  <Text variant="heading-sm" as="h2" className="mb-2">
                    Bagian 2: Pelaksanaan Tindak Lanjut
                  </Text>

                  <div className="flex items-stretch gap-2">
                    <PelaksanaanTindakLanjutStepCard
                      currentState={currentState}
                    />

                    <StepArrow />

                    <RefleksiEvaluasiStepCard
                      appealState={pelaksanaanProgressData?.data?.appealState}
                      currentState={currentState}
                      latestFormState={
                        pelaksanaanProgressData?.data?.latestFormState
                      }
                    />
                  </div>
                </div>
              </div>
            </SectionCardBody>
          </SectionCard>
        </PageGuardGpkPelaksanaanStatus>
      </div>
    </main>
  )
}

function StepArrow() {
  return (
    <div className="flex items-center">
      <Icon>arrow_forward</Icon>
    </div>
  )
}

function CompletedPelaksanaanBanner() {
  return (
    <section className="mb-6 bg-white rounded-lg shadow-card">
      <div className="flex items-center p-6 space-x-6">
        <img
          height={84}
          width={84}
          src={assetUrl('/illustrations/img-thumbs-up.png')}
          className="flex-shrink-0"
          alt=""
        />
        <div className="items-center flex-1 space-y-2">
          <Text variant="heading-md" as="h2">
            Pelaksanaan Praktik Kinerja selesai!
          </Text>
          <Text color="subdued">
            Anda dapat lanjut cek kegiatan lainnya melalui menu{' '}
            <strong>Sebagai Pegawai</strong>.
          </Text>
        </div>
      </div>
    </section>
  )
}
